import { useState } from "react";
import { FileText, Download, Loader2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportGenerator = () => {
  const [generating, setGenerating] = useState(false);

  const downloadFile = (base64Data, filename, mimeType) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const generateReport = async (range, format = 'pdf') => {
    setGenerating(true);
    try {
      if (format === 'pdf') {
        const response = await axios.get(`${API_URL}/api/reports/generate?range=${range}`);
        const { pdf_base64, filename } = response.data;
        downloadFile(pdf_base64, filename, 'application/pdf');
      } else if (format === 'excel') {
        const response = await axios.get(`${API_URL}/api/reports/excel?range=${range}`);
        const { excel_base64, filename } = response.data;
        downloadFile(excel_base64, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      }
    } catch (err) {
      console.error("Error generating report:", err);
      alert("Error al generar el informe");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-white h-10 w-10 p-0"
          disabled={generating}
          data-testid="report-generator-btn"
        >
          {generating ? (
            <Loader2 className="w-[44px] h-[44px] animate-spin" />
          ) : (
            <FileText className="w-[44px] h-[44px]" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-zinc-900 border-zinc-800 w-56">
        <DropdownMenuLabel className="text-zinc-400 text-xs">
          Descargar PDF
        </DropdownMenuLabel>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('day', 'pdf')}
        >
          <FileText className="w-4 h-4 mr-2 text-red-400" />
          PDF Diario
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('week', 'pdf')}
        >
          <FileText className="w-4 h-4 mr-2 text-red-400" />
          PDF Semanal
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('month', 'pdf')}
        >
          <FileText className="w-4 h-4 mr-2 text-red-400" />
          PDF Mensual
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('year', 'pdf')}
        >
          <FileText className="w-4 h-4 mr-2 text-red-400" />
          PDF Anual
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="bg-zinc-700" />
        
        <DropdownMenuLabel className="text-zinc-400 text-xs">
          Descargar Excel
        </DropdownMenuLabel>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('day', 'excel')}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-400" />
          Excel Diario
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('week', 'excel')}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-400" />
          Excel Semanal
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('month', 'excel')}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-400" />
          Excel Mensual
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
          onClick={() => generateReport('year', 'excel')}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-400" />
          Excel Anual
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ReportGenerator;
